// TeamAnalyticsScreen.js — Full-featured team analytics with real API integration
import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, FlatList,
  ActivityIndicator, Modal, Share, Alert, Dimensions, RefreshControl,
 Linking, 
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');
const getBusinessId=async()=>{try{return await AsyncStorage.getItem('company_id')||await AsyncStorage.getItem('company_id')||'0';}catch{return '0';}};
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ── API ──────────────────────────────────────────────────────────────────────
const fetchCompanyProfile = async () => {
  try {
    const bizId = await getBusinessId();
    const res = await fetch(`${API_BASE}/api/company-profile/${parseInt(bizId)||0}`, { headers: await getH() });
    if (res.ok) { const j = await res.json(); const p = j?.success ? (Array.isArray(j.data)?j.data[0]:j.data) : null; if(p) await AsyncStorage.setItem('company_profile', JSON.stringify(p)); return p; }
  } catch {} 
  try { const c = await AsyncStorage.getItem('company_profile'); return c ? JSON.parse(c) : null; } catch { return null; }
};

const api = async (path) => {
  const token = await AsyncStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

// ── Formatters ───────────────────────────────────────────────────────────────
const fmt = {
  currency: (n) => n == null ? '—' : `£${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`,
  pct:      (n) => n == null ? '—' : `${Number(n).toFixed(1)}%`,
  num:      (n) => n == null ? '—' : Number(n).toLocaleString('en-GB'),
  date:     (s) => s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—',
  days:     (s) => {
    if (!s) return '—';
    const diff = Math.floor((Date.now() - new Date(s)) / 86400000);
    return diff === 0 ? 'Today' : diff === 1 ? 'Yesterday' : `${diff}d ago`;
  },
};

const perfColor  = (v) => v >= 90 ? '#388E3C' : v >= 70 ? '#1976D2' : v >= 50 ? '#F57C00' : '#D32F2F';
const perfBg     = (v) => v >= 90 ? '#E8F5E9' : v >= 70 ? '#E3F2FD' : v >= 50 ? '#FFF3E0' : '#FFEBEE';
const trendColor = (v) => v > 0 ? '#388E3C' : v < 0 ? '#D32F2F' : '#78909C';
const trendIcon  = (v) => v > 0 ? 'trending-up' : v < 0 ? 'trending-down' : 'remove';

const PALETTE = ['#0e4e96','#1976D2','#388E3C','#F57C00','#7B1FA2','#D32F2F','#0097A7','#5D4037','#C2185B','#455A64'];
const colorFor = (str = '') => PALETTE[str.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length];

const PERIODS = [
  { key: 'week',    label: 'W'     },
  { key: 'month',   label: 'M'     },
  { key: 'quarter', label: 'Q'     },
  { key: 'year',    label: 'Y'     },
];

const TODAY = new Date().toISOString().split('T')[0];

// ── Pure display components ──────────────────────────────────────────────────
const Avatar = ({ name = '', size = 44, color }) => {
  const bg = color || colorFor(name);
  const ini = (name.split(' ').slice(0, 2).map(w => (w[0] || '').toUpperCase()).join('')) || '?';
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: bg,
      alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ color: '#fff', fontSize: size * 0.36, fontWeight: '800' }}>{ini}</Text>
    </View>
  );
};

const HBar = ({ value = 0, max = 100, color = '#0e4e96', height = 8 }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <View style={{ height, backgroundColor: '#ECEFF1', borderRadius: height / 2, overflow: 'hidden' }}>
      <View style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: height / 2 }} />
    </View>
  );
};

// Inline bar chart using pure RN views
const MiniBarChart = ({ data = [], color = '#0e4e96', height = 50 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value || 0), 1);
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-end', height, gap: 4 }}>
      {data.map((d, i) => {
        const barH = Math.max((d.value / max) * height, 2);
        return (
          <View key={i} style={{ flex: 1, alignItems: 'center' }}>
            <View style={{ width: '100%', height: barH, backgroundColor: color,
              borderTopLeftRadius: 3, borderTopRightRadius: 3, opacity: 0.7 + (i / data.length) * 0.3 }} />
          </View>
        );
      })}
    </View>
  );
};

// Sparkline (dots connected conceptually by y-position)
const Sparkline = ({ data = [], color = '#0e4e96', height = 30, width: w = 80 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const step = w / Math.max(data.length - 1, 1);
  return (
    <View style={{ width: w, height, flexDirection: 'row', alignItems: 'flex-end', gap: 0 }}>
      {data.map((v, i) => {
        const h = Math.max((v / max) * height, 2);
        return <View key={i} style={{ flex: 1, height: h, backgroundColor: color, opacity: 0.5 + (i / data.length) * 0.5 }} />;
      })}
    </View>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    active:   { bg: '#E8F5E9', fg: '#2E7D32' },
    inactive: { bg: '#FFEBEE', fg: '#C62828' },
    on_leave: { bg: '#FFF3E0', fg: '#E65100' },
    remote:   { bg: '#E3F2FD', fg: '#1565C0' },
  };
  const s = map[status] || { bg: '#F5F5F5', fg: '#616161' };
  return (
    <View style={{ backgroundColor: s.bg, borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2 }}>
      <Text style={{ color: s.fg, fontSize: 10, fontWeight: '700', textTransform: 'capitalize' }}>
        {(status || '').replace('_', ' ')}
      </Text>
    </View>
  );
};

const KpiCard = ({ icon, label, value, subValue, change, color, onPress }) => (
  <TouchableOpacity onPress={onPress} activeOpacity={onPress ? 0.7 : 1}
    style={[AS.kpiCard, { borderTopColor: color, borderTopWidth: 3 }]}>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
      <View style={[AS.kpiIcon, { backgroundColor: color + '22' }]}>
        <Ionicons name={icon} size={18} color={color} />
      </View>
      {change != null && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2,
          backgroundColor: (change > 0 ? '#E8F5E9' : change < 0 ? '#FFEBEE' : '#F5F5F5'), borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2 }}>
          <Ionicons name={trendIcon(change)} size={11} color={trendColor(change)} />
          <Text style={{ fontSize: 10, color: trendColor(change), fontWeight: '700' }}>
            {change > 0 ? '+' : ''}{change?.toFixed(1)}%
          </Text>
        </View>
      )}
    </View>
    <Text style={AS.kpiValue}>{value}</Text>
    <Text style={AS.kpiLabel}>{label}</Text>
    {subValue && <Text style={{ fontSize: 11, color: '#78909C', marginTop: 2 }}>{subValue}</Text>}
  </TouchableOpacity>
);

const SectionCard = ({ title, children, action, actionLabel }) => (
  <View style={AS.sectionCard}>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <Text style={AS.sectionTitle}>{title}</Text>
      {action && <TouchableOpacity onPress={action}><Text style={AS.sectionLink}>{actionLabel || 'See all →'}</Text></TouchableOpacity>}
    </View>
    {children}
  </View>
);

const Divider = () => <View style={{ height: 1, backgroundColor: '#F5F7FA', marginVertical: 5 }} />;

const MetricRow = ({ label, value, color }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: '#F5F7FA' }}>
    <Text style={{ fontSize: 13, color: '#546E7A' }}>{label}</Text>
    <Text style={{ fontSize: 13, fontWeight: '700', color: color || '#263238' }}>{value}</Text>
  </View>
);

// ── Main Component ───────────────────────────────────────────────────────────
export default function TeamAnalyticsScreen({ navigation }) {
  const [companyId, setCompanyId] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [refreshing,setRefreshing]= useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [period,    setPeriod]    = useState('month');
  const [sortBy,    setSortBy]    = useState('performance');
  const [sortDir,   setSortDir]   = useState('desc');

  // Modals
  const [memberModal, setMemberModal] = useState(false);
  const [selMember,   setSelMember]   = useState(null);
  const [deptModal,   setDeptModal]   = useState(false);
  const [selDept,     setSelDept]     = useState(null);
  const [detailTab,   setDetailTab]   = useState('metrics');

  // Data
  const [dashboard,   setDashboard]   = useState({});
  const [members,     setMembers]     = useState([]);
  const [performance, setPerformance] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [meetings,    setMeetings]    = useState([]);

  const lock = useRef(false);

  // ── Bootstrap ────────────────────────────────────────────────────────────────
  useEffect(() => {
    AsyncStorage.getItem('company_id').then(id => { if (id) setCompanyId(id); });
  }, []);

  useEffect(() => { if (companyId) fetchAll(); }, [companyId]);

  useEffect(() => {
    const unsub = navigation.addListener('focus', () => { if (companyId) fetchAll(false); });
    return unsub;
  }, [navigation, companyId]);

  // ── Load ─────────────────────────────────────────────────────────────────────
  const fetchAll = async (spinner = true) => {
    if (lock.current) return;
    lock.current = true;
    if (spinner) setLoading(true);
    try {
      await Promise.all([
        fetchDashboard(), fetchMembers(), fetchPerformance(), fetchDepartments(), fetchMeetings(),
      ]);
    } finally { setLoading(false); lock.current = false; }
  };

  const fetchDashboard = async () => {
    try {
      const r = await api(`/api/data/team_analytics_dashboard?company_id=${companyId}`);
      setDashboard(r.data ?? r ?? {});
    } catch { }
  };

  const fetchMembers = async () => {
    try {
      const r = await api(`/api/data/team_members?company_id=${companyId}`);
      setMembers(Array.isArray(r) ? r : (r.data ?? r.team_members ?? []));
    } catch { }
  };

  const fetchPerformance = async () => {
    try {
      const r = await api(`/api/data/team_members_performance?company_id=${companyId}`);
      setPerformance(Array.isArray(r) ? r : (r.data ?? r.performance ?? []));
    } catch { }
  };

  const fetchDepartments = async () => {
    try {
      const r = await api(`/api/data/team_departments?company_id=${companyId}`);
      setDepartments(Array.isArray(r) ? r : (r.data ?? r.departments ?? []));
    } catch { }
  };

  const fetchMeetings = async () => {
    try {
      const r = await api(`/api/data/team_meetings?company_id=${companyId}&meeting_date>=${TODAY}`);
      setMeetings(Array.isArray(r) ? r : (r.data ?? r.meetings ?? []));
    } catch { }
  };

  const onRefresh = async () => { setRefreshing(true); await fetchAll(false); setRefreshing(false); };

  // ── Enriched data ────────────────────────────────────────────────────────────
  const enriched = members.map(m => {
    const p = performance.find(x => x.member_id === m.id || x.team_member_id === m.id) || {};
    const d = departments.find(x => x.name === m.department);
    return {
      ...m, ...p,
      perf:       m.performance_score || m.performance || p.performance_score || 0,
      revenue:    m.revenue    || p.revenue    || 0,
      target:     m.target     || p.target     || 0,
      deals:      m.deals_count|| m.deals || p.deals_count || 0,
      conversion: m.conversion_rate || p.conversion_rate || 0,
      activities: m.activities_count || p.activities_count || 0,
      responseTime: m.response_time || p.response_time || 0,
      deptColor:  d?.color || colorFor(m.department || ''),
    };
  });

  const sorted = [...enriched].sort((a, b) => {
    const keys = { performance: 'perf', revenue: 'revenue', deals: 'deals', name: 'name', conversion: 'conversion' };
    const k = keys[sortBy] || 'perf';
    const diff = k === 'name' ? (a.name || '').localeCompare(b.name || '') : (b[k] || 0) - (a[k] || 0);
    return sortDir === 'asc' ? -diff : diff;
  });

  // ── Aggregate stats ──────────────────────────────────────────────────────────
  const total       = members.length;
  const active      = members.filter(m => m.status === 'active').length;
  const remote      = members.filter(m => m.status === 'remote').length;
  const avgPerf     = total > 0 ? Math.round(enriched.reduce((s, m) => s + m.perf, 0) / total) : 0;
  const totalRev    = enriched.reduce((s, m) => s + m.revenue, 0);
  const totalDeals  = enriched.reduce((s, m) => s + m.deals, 0);
  const totalTarget = enriched.reduce((s, m) => s + m.target, 0);
  const avgConv     = total > 0 ? enriched.reduce((s, m) => s + m.conversion, 0) / total : 0;
  const avgResp     = total > 0 ? enriched.reduce((s, m) => s + m.responseTime, 0) / total : 0;
  const totalAct    = enriched.reduce((s, m) => s + m.activities, 0);
  const totalPayroll= members.reduce((s, m) => s + (m.salary || 0), 0);

  const deptMembers = (name) => enriched.filter(m => m.department === name);
  const deptAvgPerf = (name) => {
    const ms = deptMembers(name);
    return ms.length > 0 ? Math.round(ms.reduce((s, m) => s + m.perf, 0) / ms.length) : 0;
  };
  const deptRevenue = (name) => deptMembers(name).reduce((s, m) => s + m.revenue, 0);
  const deptColor   = (name) => {
    const d = departments.find(x => x.name === name);
    return d?.color || colorFor(name);
  };

  const topPerformer = sorted[0];
  const topRevenue   = [...enriched].sort((a, b) => b.revenue - a.revenue)[0];
  const targetAchievement = totalTarget > 0 ? Math.round((totalRev / totalTarget) * 100) : null;

  // Performance distribution bands
  const perfBands = [
    { label: 'Excellent', min: 90, max: 100, color: '#388E3C' },
    { label: 'Good',      min: 70, max: 89,  color: '#1976D2' },
    { label: 'Average',   min: 50, max: 69,  color: '#F57C00' },
    { label: 'Below Avg', min: 1,  max: 49,  color: '#D32F2F' },
    { label: 'No Data',   min: 0,  max: 0,   color: '#90A4AE' },
  ];
  const bandCount = (min, max) => {
    if (min === 0 && max === 0) return enriched.filter(m => m.perf === 0).length;
    return enriched.filter(m => m.perf >= min && m.perf <= max).length;
  };

  // Skills frequency
  const allSkills = enriched.flatMap(m =>
    Array.isArray(m.skills) ? m.skills : (m.skills ? String(m.skills).split(',').map(s => s.trim()) : [])
  ).filter(Boolean);
  const skillFreq = allSkills.reduce((acc, s) => { acc[s] = (acc[s] || 0) + 1; return acc; }, {});
  const topSkills = Object.entries(skillFreq).sort((a, b) => b[1] - a[1]).slice(0, 10);

  // Contract type breakdown
  const contractBreakdown = ['Permanent','Fixed-term','Contractor','Part-time','Intern','Zero-hours'].map(c => ({
    label: c,
    count: members.filter(m => m.contract_type === c).length,
  })).filter(x => x.count > 0);

  // Department enriched
  const deptEnriched = departments.map((d, i) => {
    const ms      = deptMembers(d.name);
    const avgP    = ms.length > 0 ? Math.round(ms.reduce((s, m) => s + m.perf, 0) / ms.length) : 0;
    const rev     = ms.reduce((s, m) => s + m.revenue, 0);
    const deals   = ms.reduce((s, m) => s + m.deals, 0);
    const payroll = ms.reduce((s, m) => s + (m.salary || 0), 0);
    const avgConvD = ms.length > 0 ? ms.reduce((s, m) => s + m.conversion, 0) / ms.length : 0;
    const target  = ms.reduce((s, m) => s + m.target, 0);
    return {
      ...d, count: ms.length, avgPerf: avgP, revenue: rev, deals, payroll,
      avgConv: avgConvD, target,
      color: d.color || PALETTE[i % PALETTE.length],
      achievement: target > 0 ? Math.round((rev / target) * 100) : null,
    };
  }).sort((a, b) => b.avgPerf - a.avgPerf);

  // ── Tab: Overview ────────────────────────────────────────────────────────────
  const renderOverview = () => (
    <ScrollView showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}>

      {/* Period selector */}
      <View style={AS.periodRow}>
        <Text style={{ fontSize: 13, color: '#78909C', fontWeight: '600' }}>Period:</Text>
        <View style={AS.periodGroup}>
          {PERIODS.map(p => (
            <TouchableOpacity key={p.key} onPress={() => setPeriod(p.key)}
              style={[AS.periodBtn, period === p.key && AS.periodBtnActive]}>
              <Text style={[AS.periodText, period === p.key && AS.periodTextActive]}>{p.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <TouchableOpacity onPress={() => Share.share({ message: buildExportSummary() })} style={AS.shareBtn}>
          <Ionicons name="share-outline" size={16} color="#0e4e96" />
          <Text style={{ fontSize: 12, color: '#0e4e96', fontWeight: '700' }}>Export</Text>
        </TouchableOpacity>
      </View>

      {/* Hero */}
      <LinearGradient colors={['#1a237e','#283593']} style={AS.heroBanner}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <View>
            <Text style={{ color: '#fff', fontSize: 20, fontWeight: '900' }}>Analytics</Text>
            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 }}>
              {total} members · {departments.length} departments
            </Text>
          </View>
          {topPerformer && (
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, fontWeight: '600' }}>TOP PERFORMER</Text>
              <Text style={{ color: '#FFD54F', fontSize: 13, fontWeight: '800', marginTop: 2 }}>🏆 {topPerformer.name}</Text>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>{topPerformer.perf}%</Text>
            </View>
          )}
        </View>

        {/* Mini bar chart for department performance */}
        {deptEnriched.length > 1 && (
          <View style={{ marginBottom: 16 }}>
            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, fontWeight: '700', marginBottom: 8 }}>DEPT PERFORMANCE</Text>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 6, height: 40 }}>
              {deptEnriched.map((d, i) => {
                const barH = avgPerf > 0 ? Math.max((d.avgPerf / 100) * 40, 4) : 4;
                return (
                  <View key={d.id || i} style={{ flex: 1, alignItems: 'center', gap: 2 }}>
                    <View style={{ width: '100%', height: barH, backgroundColor: d.color,
                      borderTopLeftRadius: 3, borderTopRightRadius: 3, opacity: 0.85 }} />
                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 8 }} numberOfLines={1}>
                      {(d.name || '').split(' ')[0]}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          {[
            { label: 'Active',   value: `${active}/${total}` },
            { label: 'Avg Perf', value: `${avgPerf}%`        },
            { label: 'Revenue',  value: fmt.currency(totalRev) },
            { label: 'Deals',    value: fmt.num(totalDeals)  },
          ].map(s => (
            <View key={s.label} style={{ alignItems: 'center' }}>
              <Text style={{ color: '#fff', fontSize: 16, fontWeight: '900' }}>{s.value}</Text>
              <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, marginTop: 2 }}>{s.label}</Text>
            </View>
          ))}
        </View>

        {/* Target progress */}
        {targetAchievement != null && (
          <View style={{ marginTop: 14, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 10, padding: 10 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>Target Achievement</Text>
              <Text style={{ color: targetAchievement >= 100 ? '#69F0AE' : '#FFD54F', fontSize: 12, fontWeight: '800' }}>
                {targetAchievement}% of {fmt.currency(totalTarget)}
              </Text>
            </View>
            <View style={{ height: 8, backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 4, overflow: 'hidden' }}>
              <View style={{ width: `${Math.min(targetAchievement, 100)}%`, height: '100%',
                backgroundColor: targetAchievement >= 100 ? '#69F0AE' : '#FFD54F', borderRadius: 4 }} />
            </View>
          </View>
        )}
      </LinearGradient>

      {/* KPI grid */}
      <View style={AS.kpiGrid}>
        <KpiCard icon="people"         label="Active Members"    value={active}
          subValue={`${remote} remote`} change={dashboard.members_growth} color="#1976D2"
          onPress={() => navigation.navigate('TeamManagementScreen')} />
        <KpiCard icon="speedometer"    label="Avg Performance"   value={`${avgPerf}%`}
          change={dashboard.performance_growth} color="#388E3C"
          onPress={() => setActiveTab('performance')} />
        <KpiCard icon="cash"           label="Total Revenue"     value={fmt.currency(totalRev)}
          subValue={`${totalDeals} deals`} change={dashboard.revenue_growth} color="#F57C00"
          onPress={() => setActiveTab('members')} />
        <KpiCard icon="sync"           label="Avg Conversion"    value={fmt.pct(avgConv)}
          change={dashboard.conversion_growth} color="#7B1FA2"
          onPress={() => setActiveTab('members')} />
        <KpiCard icon="time"           label="Avg Response"      value={avgResp > 0 ? `${avgResp.toFixed(1)}h` : '—'}
          change={dashboard.response_growth} color="#0097A7" />
        <KpiCard icon="checkmark-done" label="Total Activities"  value={fmt.num(totalAct)}
          change={dashboard.activities_growth} color="#C2185B" />
      </View>

      {/* Top performers leaderboard */}
      {sorted.length > 0 && (
        <SectionCard title="🏆 Leaderboard" action={() => setActiveTab('members')} actionLabel="Full Rankings →">
          {sorted.slice(0, 5).map((m, i) => (
            <TouchableOpacity key={m.id} style={AS.leaderRow}
              onPress={() => { setSelMember(m); setMemberModal(true); setDetailTab('metrics'); }}
              onLongPress={() => navigation.navigate('BusinessVideoConferenceScreen', { participantName: m.name, companyId })}>
              <Text style={[AS.rank, { color: i < 3 ? '#F57C00' : '#B0BEC5' }]}>#{i + 1}</Text>
              <Avatar name={m.name} size={36} color={m.deptColor} />
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={AS.lName}>{m.name}</Text>
                <Text style={AS.lSub}>{m.role || m.department || '—'}</Text>
                <HBar value={m.perf} max={100} color={perfColor(m.perf)} height={5} />
              </View>
              <View style={{ alignItems: 'flex-end', marginLeft: 10 }}>
                <Text style={[AS.lPerf, { color: perfColor(m.perf) }]}>{m.perf}%</Text>
                {m.revenue > 0 && <Text style={AS.lRev}>{fmt.currency(m.revenue)}</Text>}
              </View>
            </TouchableOpacity>
          ))}
        </SectionCard>
      )}

      {/* Department overview */}
      {deptEnriched.length > 0 && (
        <SectionCard title="🏢 Department Summary" action={() => setActiveTab('departments')} actionLabel="Details →">
          {deptEnriched.map((d, i) => (
            <TouchableOpacity key={d.id || i} style={AS.deptRow}
              onPress={() => { setSelDept(d); setDeptModal(true); }}>
              <View style={[AS.deptDot, { backgroundColor: d.color }]} />
              <View style={{ flex: 1 }}>
                <Text style={AS.deptName}>{d.name}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 3 }}>
                  <HBar value={d.avgPerf} max={100} color={d.color} height={5} />
                  <Text style={{ fontSize: 11, color: d.color, fontWeight: '700', minWidth: 36 }}>{d.avgPerf}%</Text>
                </View>
              </View>
              <View style={{ alignItems: 'flex-end', marginLeft: 10 }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#263238' }}>{d.count}</Text>
                <Text style={{ fontSize: 10, color: '#78909C' }}>members</Text>
              </View>
            </TouchableOpacity>
          ))}
        </SectionCard>
      )}

      {/* Meetings snippet */}
      {meetings.length > 0 && (
        <SectionCard title="📅 Upcoming Meetings">
          {meetings.slice(0, 4).map((m, i) => (
            <View key={m.id || i}>
              <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 9, gap: 10 }}>
                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#0e4e96' }} />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 13, fontWeight: '600', color: '#263238' }}>{m.title}</Text>
                  <Text style={{ fontSize: 11, color: '#78909C', marginTop: 1 }}>{m.meeting_date} · {m.time || ''}</Text>
                </View>
                {(m.participants || []).length > 0 && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, marginRight: 4 }}>
                    <Ionicons name="people-outline" size={12} color="#78909C" />
                    <Text style={{ fontSize: 11, color: '#78909C' }}>{m.participants.length}</Text>
                  </View>
                )}
                <TouchableOpacity
                  style={{ backgroundColor: '#0e4e96', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 5,
                    flexDirection: 'row', alignItems: 'center', gap: 4 }}
                  onPress={() => navigation.navigate('BusinessVideoConferenceScreen', {
                    meetingId: m.id, meetingTitle: m.title, companyId,
                  })}>
                  <Ionicons name="videocam" size={12} color="#fff" />
                  <Text style={{ fontSize: 11, color: '#fff', fontWeight: '700' }}>Join</Text>
                </TouchableOpacity>
              </View>
              {i < Math.min(meetings.length, 4) - 1 && <Divider />}
            </View>
          ))}
          <TouchableOpacity
            style={{ paddingTop: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5 }}
            onPress={() => navigation.navigate('BusinessVideoConferenceScreen', { companyId, autoJoin: false })}>
            <Ionicons name="add-circle-outline" size={15} color="#0e4e96" />
            <Text style={{ fontSize: 12, color: '#0e4e96', fontWeight: '700' }}>Schedule New Meeting</Text>
          </TouchableOpacity>
        </SectionCard>
      )}

      {/* Payroll insight */}
      {totalPayroll > 0 && (
        <SectionCard title="💰 Payroll Insight">
          <View style={{ flexDirection: 'row', gap: 10, marginBottom: 12 }}>
            <View style={[AS.statBox, { flex: 1 }]}>
              <Text style={AS.statBoxLabel}>Annual Total</Text>
              <Text style={[AS.statBoxValue, { color: '#0e4e96' }]}>{fmt.currency(totalPayroll)}</Text>
            </View>
            <View style={[AS.statBox, { flex: 1 }]}>
              <Text style={AS.statBoxLabel}>Per Head</Text>
              <Text style={[AS.statBoxValue, { color: '#0e4e96' }]}>
                {total > 0 ? fmt.currency(Math.round(totalPayroll / total)) : '—'}
              </Text>
            </View>
            <View style={[AS.statBox, { flex: 1 }]}>
              <Text style={AS.statBoxLabel}>Revenue/Pay</Text>
              <Text style={[AS.statBoxValue, { color: totalRev > totalPayroll ? '#388E3C' : '#D32F2F' }]}>
                <Text>{totalPayroll > 0 ? `${(totalRev / totalPayroll).toFixed(1)}x` : '—'}</Text>
              </Text>
            </View>
          </View>
          {contractBreakdown.map(c => (
            <View key={c.label} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6, gap: 10 }}>
              <Text style={{ fontSize: 12, color: '#546E7A', width: 100 }}>{c.label}</Text>
              <View style={{ flex: 1 }}>
                <HBar value={c.count} max={total} color="#0e4e96" height={6} />
              </View>
              <Text style={{ fontSize: 12, fontWeight: '700', color: '#0e4e96', width: 24, textAlign: 'right' }}>{c.count}</Text>
            </View>
          ))}
        </SectionCard>
      )}

      {/* Skills cloud */}
      {topSkills.length > 0 && (
        <SectionCard title="🧠 Skills in Team">
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {topSkills.map(([skill, count]) => (
              <View key={skill} style={[AS.skillChip, { opacity: 0.6 + (count / topSkills[0][1]) * 0.4 }]}>
                <Text style={AS.skillText}>{skill}</Text>
                <Text style={{ fontSize: 9, color: '#78909C', marginLeft: 4 }}>{count}</Text>
              </View>
            ))}
          </View>
        </SectionCard>
      )}

      <View style={{ height: 24 }} />
    </ScrollView>
  );

  // ── Tab: Members ─────────────────────────────────────────────────────────────
  const renderMembers = () => (
    <View style={{ flex: 1 }}>
      {/* Sort controls */}
      <View style={AS.sortBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6, paddingHorizontal: 14 }}>
          {[
            { key: 'performance', label: 'Performance' },
            { key: 'revenue',     label: 'Revenue'     },
            { key: 'deals',       label: 'Deals'       },
            { key: 'conversion',  label: 'Conversion'  },
            { key: 'name',        label: 'Name'        },
          ].map(s => (
            <TouchableOpacity key={s.key}
              style={[AS.sortBtn, sortBy === s.key && AS.sortBtnActive]}
              onPress={() => { if (sortBy === s.key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortBy(s.key); setSortDir('desc'); } }}>
              <Text style={[AS.sortBtnText, sortBy === s.key && AS.sortBtnTextActive]}>
                {s.label}{sortBy === s.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <FlatList
        data={sorted}
        keyExtractor={m => String(m.id)}
        contentContainerStyle={{ padding: 14, paddingBottom: 30 }}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        ListHeaderComponent={
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <Text style={{ fontSize: 12, color: '#78909C' }}>{sorted.length} members</Text>
            <TouchableOpacity onPress={() => Share.share({ message: buildMemberExport() })} style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
              <Ionicons name="download-outline" size={15} color="#0e4e96" />
              <Text style={{ fontSize: 12, color: '#0e4e96', fontWeight: '600' }}>Export CSV</Text>
            </TouchableOpacity>
          </View>
        }
        ListEmptyComponent={
          <View style={{ alignItems: 'center', paddingVertical: 40 }}>
            <Ionicons name="people-outline" size={48} color="#CFD8DC" />
            <Text style={{ fontSize: 15, color: '#90A4AE', marginTop: 10 }}>No member data</Text>
          </View>
        }
        renderItem={({ item: m, index: i }) => (
          <TouchableOpacity style={AS.memberCard}
            onPress={() => { setSelMember(m); setMemberModal(true); setDetailTab('metrics'); }}>
            {/* Rank + avatar + name */}
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
              <View style={[AS.rankBadge, { backgroundColor: i < 3 ? '#FFF8E1' : '#F5F7FA' }]}>
                <Text style={[AS.rankBadgeText, { color: i < 3 ? '#F57C00' : '#B0BEC5' }]}>#{i + 1}</Text>
              </View>
              <Avatar name={m.name} size={44} color={m.deptColor} />
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={AS.lName}>{m.name}</Text>
                <Text style={AS.lSub}>{m.role || '—'}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3 }}>
                  {m.department && <Text style={{ fontSize: 11, color: m.deptColor, fontWeight: '600' }}>{m.department}</Text>}
                  <StatusPill status={m.status} />
                </View>
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <Text style={[AS.lPerf, { color: perfColor(m.perf) }]}>{m.perf}%</Text>
                {m.trend != null && (
                  <Ionicons name={trendIcon(m.trend)} size={16} color={trendColor(m.trend)} style={{ marginTop: 2 }} />
                )}
              </View>
            </View>

            {/* Metrics 2×2 */}
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
              {[
                { label: 'Revenue',    value: fmt.currency(m.revenue)  },
                { label: 'Deals',      value: fmt.num(m.deals)         },
                { label: 'Conversion', value: fmt.pct(m.conversion)    },
                { label: 'Activities', value: fmt.num(m.activities)    },
              ].map(({ label, value }) => (
                <View key={label} style={[AS.metricBox, { flex: 1 }]}>
                  <Text style={AS.metricLabel}>{label}</Text>
                  <Text style={AS.metricValue}>{value}</Text>
                </View>
              ))}
            </View>

            {/* Performance bar */}
            <View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ fontSize: 11, color: '#78909C' }}>Performance Score</Text>
                <Text style={{ fontSize: 11, fontWeight: '700', color: perfColor(m.perf) }}>{m.perf}%</Text>
              </View>
              <HBar value={m.perf} max={100} color={perfColor(m.perf)} height={8} />
            </View>

            {/* Target achievement */}
            {m.target > 0 && m.revenue > 0 && (
              <View style={{ marginTop: 8 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 11, color: '#78909C' }}>Target: {fmt.currency(m.target)}</Text>
                  <Text style={{ fontSize: 11, fontWeight: '700', color: '#0e4e96' }}>
                    {Math.round((m.revenue / m.target) * 100)}%
                  </Text>
                </View>
                <HBar value={m.revenue} max={m.target} color="#0e4e96" height={6} />
              </View>
            )}

            {/* Response time if available */}
            {m.responseTime > 0 && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8 }}>
                <Ionicons name="time-outline" size={12} color="#78909C" />
                <Text style={{ fontSize: 11, color: '#78909C' }}>Avg response: {m.responseTime}h</Text>
              </View>
            )}
          </TouchableOpacity>
        )}
      />
    </View>
  );

  // ── Tab: Departments ─────────────────────────────────────────────────────────
  const renderDepartments = () => (
    <FlatList
      data={deptEnriched}
      keyExtractor={d => String(d.id || d.name)}
      contentContainerStyle={{ padding: 14, paddingBottom: 30 }}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      ListEmptyComponent={
        <View style={{ alignItems: 'center', paddingVertical: 48 }}>
          <Ionicons name="business-outline" size={48} color="#CFD8DC" />
          <Text style={{ fontSize: 15, color: '#90A4AE', marginTop: 10 }}>No department data</Text>
        </View>
      }
      renderItem={({ item: d }) => (
        <TouchableOpacity style={[AS.deptCard, { borderLeftColor: d.color }]}
          onPress={() => { setSelDept(d); setDeptModal(true); }}>
          {/* Header */}
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 14 }}>
            <LinearGradient colors={[d.color, d.color + '99']} style={AS.deptCardIcon}>
              <Ionicons name="business" size={20} color="#fff" />
            </LinearGradient>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={AS.deptCardName}>{d.name}</Text>
              {d.head ? <Text style={{ fontSize: 12, color: '#78909C' }}>{d.head}</Text> : null}
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={{ fontSize: 24, fontWeight: '900', color: perfColor(d.avgPerf) }}>{d.avgPerf}%</Text>
              {d.achievement != null && (
                <Text style={{ fontSize: 11, color: d.achievement >= 100 ? '#388E3C' : '#F57C00' }}>
                  {d.achievement}% of target
                </Text>
              )}
            </View>
          </View>

          {/* Metrics */}
          <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
            {[
              { label: 'Members',  value: fmt.num(d.count)   },
              { label: 'Revenue',  value: fmt.currency(d.revenue) },
              { label: 'Deals',    value: fmt.num(d.deals)   },
              { label: 'Payroll',  value: d.payroll > 0 ? fmt.currency(d.payroll) : '—' },
            ].map(({ label, value }) => (
              <View key={label} style={[AS.metricBox, { flex: 1 }]}>
                <Text style={AS.metricLabel}>{label}</Text>
                <Text style={AS.metricValue}>{value}</Text>
              </View>
            ))}
          </View>

          {/* Performance bar */}
          <View style={{ marginBottom: 10 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 11, color: '#78909C' }}>Team Performance</Text>
              <Text style={{ fontSize: 11, fontWeight: '700', color: d.color }}>{d.avgPerf}%</Text>
            </View>
            <HBar value={d.avgPerf} max={100} color={d.color} height={10} />
          </View>

          {/* Avg conversion */}
          {d.avgConv > 0 && (
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6,
              borderTopWidth: 1, borderTopColor: '#F5F7FA' }}>
              <Text style={{ fontSize: 12, color: '#78909C' }}>Avg Conversion</Text>
              <Text style={{ fontSize: 12, fontWeight: '700', color: '#263238' }}>{fmt.pct(d.avgConv)}</Text>
            </View>
          )}

          {/* Member avatars */}
          {d.count > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 10,
              paddingTop: 10, borderTopWidth: 1, borderTopColor: '#F5F7FA' }}>
              {deptMembers(d.name).slice(0, 7).map(m => <Avatar key={m.id} name={m.name} size={26} color={d.color} />)}
              {d.count > 7 && <Text style={{ fontSize: 11, color: '#78909C', marginLeft: 4 }}>+{d.count - 7}</Text>}
            </View>
          )}
        </TouchableOpacity>
      )}
    />
  );

  // ── Tab: Performance ─────────────────────────────────────────────────────────
  const renderPerformance = () => {
    const withPerf = enriched.filter(m => m.perf > 0);
    return (
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 14, paddingBottom: 30 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}>

        {/* Overall score */}
        <LinearGradient colors={[perfColor(avgPerf), perfColor(avgPerf) + 'AA']} style={AS.perfHero}>
          <Text style={{ color: '#fff', fontSize: 13, fontWeight: '700', marginBottom: 4 }}>TEAM PERFORMANCE</Text>
          <Text style={{ color: '#fff', fontSize: 56, fontWeight: '900' }}>{avgPerf}%</Text>
          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>Average across {total} members</Text>
          <HBar value={avgPerf} max={100} color="rgba(255,255,255,0.9)" height={10} />
        </LinearGradient>

        {/* Distribution */}
        <SectionCard title="Performance Distribution">
          {perfBands.map(band => {
            const count = bandCount(band.min, band.max);
            const pct   = total > 0 ? (count / total) * 100 : 0;
            return (
              <View key={band.label} style={{ marginBottom: 12 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: band.color }} />
                    <Text style={{ fontSize: 13, color: '#546E7A', fontWeight: '600' }}>{band.label}</Text>
                    {band.min > 0 && <Text style={{ fontSize: 11, color: '#B0BEC5' }}>({band.min}–{band.max}%)</Text>}
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text style={{ fontSize: 13, fontWeight: '800', color: band.color }}>{count}</Text>
                    <Text style={{ fontSize: 11, color: '#B0BEC5' }}>{pct.toFixed(0)}%</Text>
                  </View>
                </View>
                <HBar value={pct} max={100} color={band.color} height={10} />
              </View>
            );
          })}
        </SectionCard>

        {/* Individual ranked list */}
        <SectionCard title="Individual Rankings">
          {[...enriched].sort((a, b) => b.perf - a.perf).map((m, i) => (
            <TouchableOpacity key={m.id} style={AS.perfRow}
              onPress={() => { setSelMember(m); setMemberModal(true); setDetailTab('metrics'); }}>
              <Text style={[AS.rank, { color: i < 3 ? '#F57C00' : '#B0BEC5', width: 28 }]}>#{i + 1}</Text>
              <Avatar name={m.name} size={32} color={m.deptColor} />
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#263238' }}>{m.name}</Text>
                <Text style={{ fontSize: 11, color: '#78909C' }}>{m.department || '—'}</Text>
                <HBar value={m.perf} max={100} color={perfColor(m.perf)} height={5} />
              </View>
              <View style={[AS.perfBadge, { backgroundColor: perfBg(m.perf) }]}>
                <Text style={{ fontSize: 13, fontWeight: '800', color: perfColor(m.perf) }}>{m.perf}%</Text>
              </View>
            </TouchableOpacity>
          ))}
        </SectionCard>

        {/* Activity summary */}
        <SectionCard title="Activity Summary">
          {[
            { label: 'Total Activities', value: fmt.num(totalAct), color: '#0e4e96' },
            { label: 'Total Meetings',   value: fmt.num(meetings.length), color: '#7B1FA2' },
            { label: 'Total Revenue',    value: fmt.currency(totalRev), color: '#388E3C' },
            { label: 'Total Deals',      value: fmt.num(totalDeals), color: '#F57C00' },
            { label: 'Team Target',      value: fmt.currency(totalTarget), color: '#1976D2' },
            { label: 'Target vs Actual', value: targetAchievement != null ? `${targetAchievement}%` : '—', color: targetAchievement >= 100 ? '#388E3C' : '#F57C00' },
            { label: 'Avg Conversion',   value: fmt.pct(avgConv), color: '#0097A7' },
            { label: 'Avg Response Time',value: avgResp > 0 ? `${avgResp.toFixed(1)}h` : '—', color: '#C2185B' },
          ].map(({ label, value, color }) => <MetricRow key={label} label={label} value={value} color={color} />)}
        </SectionCard>

        {/* Revenue bar chart */}
        {enriched.some(m => m.revenue > 0) && (
          <SectionCard title="Revenue by Member">
            {[...enriched].filter(m => m.revenue > 0).sort((a, b) => b.revenue - a.revenue).slice(0, 8).map((m, i) => (
              <View key={m.id} style={{ marginBottom: 10 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 12, color: '#546E7A' }} numberOfLines={1}>{m.name}</Text>
                  <Text style={{ fontSize: 12, fontWeight: '700', color: '#0e4e96' }}>{fmt.currency(m.revenue)}</Text>
                </View>
                <HBar value={m.revenue} max={sorted.filter(x => x.revenue > 0)[0]?.revenue || 1} color={m.deptColor} height={8} />
              </View>
            ))}
          </SectionCard>
        )}
      </ScrollView>
    );
  };

  // ── Tab: Insights ─────────────────────────────────────────────────────────────
  const renderInsights = () => {
    const insights = generateInsights();
    return (
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 14, paddingBottom: 30 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}>
        <Text style={{ fontSize: 12, color: '#78909C', marginBottom: 12, fontWeight: '600' }}>
          {insights.length} insight{insights.length !== 1 ? 's' : ''} generated from live data
        </Text>
        {insights.map((ins, i) => (
          <View key={i} style={[AS.insightCard, { borderLeftColor: ins.color }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <View style={[AS.insightIcon, { backgroundColor: ins.color + '22' }]}>
                <Ionicons name={ins.icon} size={20} color={ins.color} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={AS.insightTitle}>{ins.title}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3 }}>
                  <View style={[AS.impactBadge, { backgroundColor: ins.color }]}>
                    <Text style={AS.impactText}>{String(ins.impact ?? '').toUpperCase()}</Text>
                  </View>
                  <Text style={{ fontSize: 10, color: '#78909C' }}>{ins.category}</Text>
                </View>
              </View>
            </View>
            <Text style={AS.insightBody}>{ins.body}</Text>
            {ins.onAction
              ? <TouchableOpacity style={AS.insightAction} onPress={ins.onAction}>
                  <Ionicons name="bulb-outline" size={14} color="#E65100" />
                  <Text style={[AS.insightActionText, { textDecorationLine: 'underline' }]}>{ins.action}</Text>
                </TouchableOpacity>
              : <View style={AS.insightAction}>
                  <Ionicons name="bulb-outline" size={14} color="#E65100" />
                  <Text style={AS.insightActionText}>{ins.action}</Text>
                </View>}
          </View>
        ))}
      </ScrollView>
    );
  };

  const generateInsights = () => {
    const ins = [];

    if (topPerformer && topPerformer.perf > 0) ins.push({
      title: `Top Performer: ${topPerformer.name}`,
      body: `${topPerformer.name} leads the team with a ${topPerformer.perf}% performance score${topPerformer.revenue > 0 ? ` and ${fmt.currency(topPerformer.revenue)} in revenue` : ''}.`,
      action: 'Recognise their contribution and share best practices at the next team meeting.',
      icon: 'trophy', color: '#F57C00', impact: 'positive', category: 'Performance',
    });

    const lowPerformers = enriched.filter(m => m.perf > 0 && m.perf < 50);
    if (lowPerformers.length > 0) ins.push({
      title: `${lowPerformers.length} Member${lowPerformers.length > 1 ? 's' : ''} Need Support`,
      body: `${lowPerformers.map(m => m.name).join(', ')} ${lowPerformers.length > 1 ? 'are' : 'is'} below 50% performance. Early intervention prevents further decline.`,
      action: 'Schedule 1-on-1 coaching sessions and set clear, achievable 30-day improvement targets.',
      icon: 'alert-circle', color: '#D32F2F', impact: 'critical', category: 'Performance',
    });

    if (avgPerf >= 80) ins.push({
      title: 'Strong Team Performance',
      body: `Team average performance of ${avgPerf}% exceeds the 80% benchmark. The team is performing above expectations.`,
      action: 'Maintain momentum with regular recognition programmes and development opportunities.',
      icon: 'star', color: '#388E3C', impact: 'positive', category: 'Performance',
    });

    if (targetAchievement != null && targetAchievement < 80) ins.push({
      title: 'Revenue Target at Risk',
      body: `Team is at ${targetAchievement}% of target with ${fmt.currency(totalTarget - totalRev)} still to achieve.`,
      action: 'Review pipeline, accelerate high-probability deals, and identify blockers in the sales process.',
      icon: 'flag', color: '#D32F2F', impact: 'critical', category: 'Revenue',
    });

    if (targetAchievement != null && targetAchievement >= 100) ins.push({
      title: 'Target Exceeded!',
      body: `The team has achieved ${targetAchievement}% of its revenue target, generating ${fmt.currency(totalRev)}.`,
      action: 'Set a stretch target for the next period and reward the team for exceeding expectations.',
      icon: 'checkmark-circle', color: '#388E3C', impact: 'positive', category: 'Revenue',
    });

    const activeRatio = total > 0 ? active / total : 0;
    if (activeRatio < 0.75 && total > 0) ins.push({
      title: 'Low Active Headcount',
      body: `Only ${Math.round(activeRatio * 100)}% of the team is active. ${total - active} member${total - active !== 1 ? 's' : ''} are inactive or on leave.`,
      action: 'Review inactive members — offboard if no longer relevant, or plan return-to-work for those on leave.',
      icon: 'people', color: '#F57C00', impact: 'warning', category: 'Workforce',
    });

    if (meetings.length === 0) ins.push({
      title: 'No Meetings Scheduled',
      body: 'There are no upcoming team meetings. Regular standups improve alignment and performance.',
      action: 'Tap to start an instant meeting with your team now.',
      icon: 'calendar', color: '#1976D2', impact: 'warning', category: 'Communication',
      onAction: () => navigation.navigate('BusinessVideoConferenceScreen', { companyId }),
    });

    const deptNoPerf = departments.filter(d => deptAvgPerf(d.name) === 0 && deptMembers(d.name).length > 0);
    if (deptNoPerf.length > 0) ins.push({
      title: 'Departments Missing Performance Data',
      body: `${deptNoPerf.map(d => d.name).join(', ')} have no performance scores recorded.`,
      action: 'Update performance scores for all team members to get accurate insights.',
      icon: 'information-circle', color: '#0097A7', impact: 'info', category: 'Data Quality',
    });

    if (topRevenue && topRevenue.revenue > 0) ins.push({
      title: `Top Revenue Generator: ${topRevenue.name}`,
      body: `${topRevenue.name} generated ${fmt.currency(topRevenue.revenue)} in revenue${topRevenue.deals > 0 ? ` across ${topRevenue.deals} deals` : ''}.`,
      action: 'Analyse their approach and create a playbook for the rest of the team.',
      icon: 'cash', color: '#388E3C', impact: 'positive', category: 'Revenue',
    });

    if (deptEnriched.length > 1) {
      const best  = deptEnriched[0];
      const worst = deptEnriched[deptEnriched.length - 1];
      if (best.avgPerf - worst.avgPerf > 20) ins.push({
        title: 'Performance Gap Between Departments',
        body: `${best.name} outperforms ${worst.name} by ${best.avgPerf - worst.avgPerf} percentage points.`,
        action: 'Investigate root causes of underperformance in lower-ranked departments and share techniques from top performers.',
        icon: 'analytics', color: '#7B1FA2', impact: 'warning', category: 'Departments',
      });
    }

    if (ins.length === 0) ins.push({
      title: 'Add Team Data for Insights',
      body: 'Performance insights are generated from real member data. Add team members, performance scores, and targets to see actionable insights here.',
      action: 'Go to Team Management to add or update team member profiles.',
      icon: 'analytics', color: '#455A64', impact: 'info', category: 'Getting Started',
    });

    return ins;
  };

  // ── Member Detail Modal ──────────────────────────────────────────────────────
  const renderMemberModal = () => {
    const m = selMember;
    if (!m) return null;
    const skills  = Array.isArray(m.skills) ? m.skills : (m.skills ? String(m.skills).split(',').map(s => s.trim()).filter(Boolean) : []);
    const certs   = Array.isArray(m.certifications) ? m.certifications : (m.certifications ? String(m.certifications).split(',').map(s => s.trim()).filter(Boolean) : []);
    const langs   = Array.isArray(m.languages) ? m.languages : (m.languages ? String(m.languages).split(',').map(s => s.trim()).filter(Boolean) : []);
    const TABS = ['metrics','profile','skills'];
    return (
      <Modal visible={memberModal} animationType="slide" presentationStyle="pageSheet"
        onRequestClose={() => { setMemberModal(false); setSelMember(null); }}>
        <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
          <View style={AS.mHeader}>
            <TouchableOpacity onPress={() => { setMemberModal(false); setSelMember(null); }}>
              <Ionicons name="close" size={24} color="#607D8B" />
            </TouchableOpacity>
            <Text style={AS.mHeaderTitle} numberOfLines={1}>{m.name}</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity
                onPress={() => { setMemberModal(false); navigation.navigate('BusinessVideoConferenceScreen', { participantName: m.name, companyId }); }}>
                <Ionicons name="videocam-outline" size={22} color="#F57C00" />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => Share.share({ message: `${m.name}\n${m.role || ''}\nPerformance: ${m.perf}%\nRevenue: ${fmt.currency(m.revenue)}\nDeals: ${m.deals}` })}>
                <Ionicons name="share-outline" size={22} color="#0e4e96" />
              </TouchableOpacity>
            </View>
          </View>

          {/* Hero */}
          <LinearGradient colors={[m.deptColor || '#0e4e96', (m.deptColor || '#0e4e96') + 'BB']}
            style={{ padding: 28, alignItems: 'center', gap: 6 }}>
            <Avatar name={m.name} size={72} color="rgba(255,255,255,0.2)" />
            <Text style={{ color: '#fff', fontSize: 22, fontWeight: '900', marginTop: 8 }}>{m.name}</Text>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>{m.role || '—'}</Text>
            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>{m.department || '—'}</Text>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 4, alignItems: 'center' }}>
              <StatusPill status={m.status} />
              {m.contract_type && m.contract_type !== 'Permanent' && (
                <View style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2 }}>
                  <Text style={{ color: '#fff', fontSize: 10, fontWeight: '700' }}>{m.contract_type}</Text>
                </View>
              )}
            </View>
          </LinearGradient>

          {/* Tabs */}
          <View style={{ flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1' }}>
            {TABS.map(t => (
              <TouchableOpacity key={t} style={[{ flex: 1, paddingVertical: 10, alignItems: 'center' },
                detailTab === t && { borderBottomWidth: 2, borderBottomColor: m.deptColor || '#0e4e96' }]}
                onPress={() => setDetailTab(t)}>
                <Text style={{ fontSize: 12, fontWeight: '700', textTransform: 'capitalize',
                  color: detailTab === t ? (m.deptColor || '#0e4e96') : '#90A4AE' }}>{t}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <ScrollView style={{ flex: 1, padding: 16 }}>
            {detailTab === 'metrics' && (
              <>
                {/* Big perf number */}
                <View style={{ alignItems: 'center', paddingVertical: 20, backgroundColor: '#fff', borderRadius: 16, marginBottom: 12 }}>
                  <Text style={{ fontSize: 64, fontWeight: '900', color: perfColor(m.perf) }}>{m.perf}%</Text>
                  <Text style={{ fontSize: 13, color: '#78909C' }}>Performance Score</Text>
                  <View style={{ width: '80%', marginTop: 10 }}>
                    <HBar value={m.perf} max={100} color={perfColor(m.perf)} height={12} />
                  </View>
                </View>

                {/* Metrics grid */}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  {[
                    { label: 'Revenue',        value: fmt.currency(m.revenue),    color: '#388E3C' },
                    { label: 'Target',         value: fmt.currency(m.target),     color: '#0e4e96' },
                    { label: 'Deals Closed',   value: fmt.num(m.deals),           color: '#F57C00' },
                    { label: 'Conversion',     value: fmt.pct(m.conversion),      color: '#7B1FA2' },
                    { label: 'Activities',     value: fmt.num(m.activities),      color: '#0097A7' },
                    { label: 'Response Time',  value: m.responseTime > 0 ? `${m.responseTime}h` : '—', color: '#C2185B' },
                    { label: 'Avg Deal Size',  value: m.deals > 0 ? fmt.currency(m.revenue / m.deals) : '—', color: '#5D4037' },
                    { label: 'Salary',         value: m.salary ? fmt.currency(m.salary) : '—', color: '#455A64' },
                  ].map(({ label, value, color }) => (
                    <View key={label} style={[AS.metricBox, { width: (width - 52) / 2, paddingVertical: 14 }]}>
                      <Text style={AS.metricLabel}>{label}</Text>
                      <Text style={[AS.metricValue, { fontSize: 16, color }]}>{value}</Text>
                    </View>
                  ))}
                </View>

                {/* Target bar */}
                {m.target > 0 && m.revenue > 0 && (
                  <View style={{ backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 12 }}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#263238', marginBottom: 12 }}>Target Achievement</Text>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                      <Text style={{ fontSize: 12, color: '#78909C' }}>Revenue vs Target</Text>
                      <Text style={{ fontSize: 12, fontWeight: '800', color: '#0e4e96' }}>
                        {Math.round((m.revenue / m.target) * 100)}%
                      </Text>
                    </View>
                    <HBar value={m.revenue} max={m.target} color="#0e4e96" height={12} />
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 }}>
                      <Text style={{ fontSize: 11, color: '#78909C' }}>Achieved: {fmt.currency(m.revenue)}</Text>
                      <Text style={{ fontSize: 11, color: '#78909C' }}>Target: {fmt.currency(m.target)}</Text>
                    </View>
                  </View>
                )}
              </>
            )}

            {detailTab === 'profile' && (
              <View style={{ backgroundColor: '#fff', borderRadius: 16, padding: 16 }}>
                {[
                  { icon: 'briefcase-outline',   label: 'Role',       value: m.role       },
                  { icon: 'business-outline',    label: 'Department', value: m.department },
                  { icon: 'person-outline',      label: 'Manager',    value: m.manager    },
                  { icon: 'location-outline',    label: 'Territory',  value: m.territory  },
                  { icon: 'mail-outline',        label: 'Email',      value: m.email      },
                  { icon: 'call-outline',        label: 'Phone',      value: m.phone      },
                  { icon: 'calendar-outline',    label: 'Joined',     value: fmt.date(m.join_date || m.joinDate) },
                  { icon: 'document-text-outline',label: 'Contract',  value: m.contract_type },
                  { icon: 'cash-outline',        label: 'Salary',     value: m.salary ? fmt.currency(m.salary) : null },
                ].filter(r => r.value).map(r => (
                  <View key={r.label} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 9,
                    borderBottomWidth: 1, borderBottomColor: '#F5F7FA' }}>
                    <View style={{ width: 30, height: 30, borderRadius: 15, backgroundColor: '#E3F2FD',
                      alignItems: 'center', justifyContent: 'center', marginRight: 12 }}>
                      <Ionicons name={r.icon} size={15} color="#0e4e96" />
                    </View>
                    <Text style={{ width: 90, fontSize: 12, color: '#90A4AE', fontWeight: '600' }}>{r.label}</Text>
                    <Text style={{ flex: 1, fontSize: 14, color: '#37474F', fontWeight: '500' }} numberOfLines={2}>{r.value}</Text>
                  </View>
                ))}
                {m.notes ? (
                  <View style={{ marginTop: 14 }}>
                    <Text style={{ fontSize: 12, color: '#90A4AE', fontWeight: '600', marginBottom: 6 }}>NOTES</Text>
                    <Text style={{ fontSize: 14, color: '#546E7A', lineHeight: 22 }}>{m.notes}</Text>
                  </View>
                ) : null}

                {/* Quick action buttons */}
                <View style={{ flexDirection: 'row', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                  <TouchableOpacity
                    style={{ flex: 1, minWidth: 120, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                      gap: 6, backgroundColor: '#FFF3E0', borderRadius: 10, paddingVertical: 10 }}
                    onPress={() => { setDetailMember(null); navigation.navigate('BusinessVideoConferenceScreen', { participantName: m.name, companyId }); }}>
                    <Ionicons name="videocam" size={16} color="#F57C00" />
                    <Text style={{ fontSize: 13, color: '#F57C00', fontWeight: '700' }}>Instant Call</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{ flex: 1, minWidth: 120, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                      gap: 6, backgroundColor: '#E3F2FD', borderRadius: 10, paddingVertical: 10 }}
                    onPress={() => { setDetailMember(null); navigation.navigate('BusinessUserProfileScreen', { prefillUserId: m.user_id || m.id, prefillName: m.name }); }}>
                    <Ionicons name="person-circle-outline" size={16} color="#0e4e96" />
                    <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '700' }}>Full Profile</Text>
                  </TouchableOpacity>
                  {m.email && (
                    <TouchableOpacity
                      style={{ flex: 1, minWidth: 120, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                        gap: 6, backgroundColor: '#E8F5E9', borderRadius: 10, paddingVertical: 10 }}
                      onPress={() => Linking.openURL(`mailto:${m.email}`).catch(() => {})}>
                      <Ionicons name="mail-outline" size={16} color="#388E3C" />
                      <Text style={{ fontSize: 13, color: '#388E3C', fontWeight: '700' }}>Email</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            )}

            {detailTab === 'skills' && (
              <>
                {skills.length > 0 && (
                  <View style={{ backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 12 }}>
                    <Text style={{ fontSize: 12, color: '#90A4AE', fontWeight: '700', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Skills</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                      {skills.map((s, i) => <View key={i} style={AS.skillChip}><Text style={AS.skillText}>{s}</Text></View>)}
                    </View>
                  </View>
                )}
                {certs.length > 0 && (
                  <View style={{ backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 12 }}>
                    <Text style={{ fontSize: 12, color: '#90A4AE', fontWeight: '700', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Certifications</Text>
                    {certs.map((c, i) => (
                      <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6,
                        borderBottomWidth: 1, borderBottomColor: '#F5F7FA' }}>
                        <Ionicons name="ribbon-outline" size={16} color="#F57C00" />
                        <Text style={{ fontSize: 14, color: '#37474F' }}>{c}</Text>
                      </View>
                    ))}
                  </View>
                )}
                {langs.length > 0 && (
                  <View style={{ backgroundColor: '#fff', borderRadius: 16, padding: 16 }}>
                    <Text style={{ fontSize: 12, color: '#90A4AE', fontWeight: '700', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Languages</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                      {langs.map((l, i) => (
                        <View key={i} style={[AS.skillChip, { backgroundColor: '#F3E5F5', borderColor: '#E1BEE7' }]}>
                          <Text style={[AS.skillText, { color: '#6A1B9A' }]}>{l}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}
                {skills.length === 0 && certs.length === 0 && langs.length === 0 && (
                  <View style={{ alignItems: 'center', paddingVertical: 40 }}>
                    <Ionicons name="ribbon-outline" size={48} color="#CFD8DC" />
                    <Text style={{ fontSize: 15, color: '#90A4AE', marginTop: 10 }}>No skills data</Text>
                  </View>
                )}
              </>
            )}
            <View style={{ height: 30 }} />
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ── Department Detail Modal ──────────────────────────────────────────────────
  const renderDeptModal = () => {
    const d = selDept;
    if (!d) return null;
    const ms = deptMembers(d.name);
    return (
      <Modal visible={deptModal} animationType="slide" presentationStyle="pageSheet"
        onRequestClose={() => { setDeptModal(false); setSelDept(null); }}>
        <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
          <View style={AS.mHeader}>
            <TouchableOpacity onPress={() => { setDeptModal(false); setSelDept(null); }}>
              <Ionicons name="close" size={24} color="#607D8B" />
            </TouchableOpacity>
            <Text style={AS.mHeaderTitle}>{d.name}</Text>
            <View style={{ width: 36 }} />
          </View>

          <LinearGradient colors={[d.color || '#0e4e96', (d.color || '#0e4e96') + 'BB']}
            style={{ padding: 24, alignItems: 'center', gap: 6 }}>
            <View style={{ width: 60, height: 60, borderRadius: 30, backgroundColor: 'rgba(255,255,255,0.2)',
              alignItems: 'center', justifyContent: 'center' }}>
              <Ionicons name="business" size={28} color="#fff" />
            </View>
            <Text style={{ color: '#fff', fontSize: 20, fontWeight: '900', marginTop: 8 }}>{d.name}</Text>
            {d.head && <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Head: {d.head}</Text>}
            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{d.count} members</Text>
          </LinearGradient>

          <ScrollView style={{ flex: 1, padding: 16 }}>
            {/* Performance overview */}
            <View style={{ backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 12, alignItems: 'center' }}>
              <Text style={{ fontSize: 52, fontWeight: '900', color: perfColor(d.avgPerf) }}>{d.avgPerf}%</Text>
              <Text style={{ fontSize: 13, color: '#78909C', marginBottom: 10 }}>Average Performance</Text>
              <View style={{ width: '90%' }}><HBar value={d.avgPerf} max={100} color={d.color} height={12} /></View>
            </View>

            {/* Stats grid */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
              {[
                { label: 'Members',    value: fmt.num(d.count)         },
                { label: 'Revenue',    value: fmt.currency(d.revenue)  },
                { label: 'Deals',      value: fmt.num(d.deals)         },
                { label: 'Payroll',    value: fmt.currency(d.payroll)  },
                { label: 'Avg Conv',   value: fmt.pct(d.avgConv)       },
                { label: 'Target',     value: fmt.currency(d.target)   },
                { label: 'Achievement',value: d.achievement != null ? `${d.achievement}%` : '—' },
                { label: 'Budget',     value: d.budget ? fmt.currency(d.budget) : '—' },
              ].map(({ label, value }) => (
                <View key={label} style={[AS.metricBox, { width: (width - 52) / 2 }]}>
                  <Text style={AS.metricLabel}>{label}</Text>
                  <Text style={[AS.metricValue, { color: d.color, fontSize: 15 }]}>{value}</Text>
                </View>
              ))}
            </View>

            {/* Member list */}
            <View style={{ backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 24 }}>
              <Text style={{ fontSize: 13, fontWeight: '700', color: '#263238', marginBottom: 12 }}>Team Members</Text>
              {ms.length === 0 ? (
                <Text style={{ fontSize: 13, color: '#B0BEC5' }}>No members in this department</Text>
              ) : (
                [...ms].sort((a, b) => b.perf - a.perf).map((m, i) => (
                  <TouchableOpacity key={m.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 10,
                    borderBottomWidth: 1, borderBottomColor: '#F5F7FA' }}
                    onPress={() => { setSelMember(m); setDetailTab('metrics'); }}>
                    <Avatar name={m.name} size={36} color={d.color} />
                    <View style={{ flex: 1, marginLeft: 10 }}>
                      <Text style={{ fontSize: 13, fontWeight: '700', color: '#263238' }}>{m.name}</Text>
                      <Text style={{ fontSize: 11, color: '#78909C' }}>{m.role || '—'}</Text>
                    </View>
                    <Text style={{ fontSize: 13, fontWeight: '800', color: perfColor(m.perf) }}>{m.perf}%</Text>
                  </TouchableOpacity>
                ))
              )}
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const buildExportSummary = () =>
    `Team Analytics Report\n\nTotal Members: ${total}\nActive: ${active}\nAvg Performance: ${avgPerf}%\nTotal Revenue: ${fmt.currency(totalRev)}\nTotal Deals: ${totalDeals}\nDepartments: ${departments.length}\n\nTop Performer: ${topPerformer?.name || '—'} (${topPerformer?.perf || 0}%)\nTop Revenue: ${topRevenue?.name || '—'} (${fmt.currency(topRevenue?.revenue || 0)})`;

  const buildMemberExport = () => {
    const header = 'Name,Role,Department,Status,Performance,Revenue,Deals,Email';
    const rows   = sorted.map(m => `${m.name},${m.role || ''},${m.department || ''},${m.status || ''},${m.perf}%,${m.revenue || 0},${m.deals || 0},${m.email || ''}`);
    return [header, ...rows].join('\n');
  };

  // ── Main render ──────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'overview',    label: 'Overview',    icon: 'home-outline'      },
    { id: 'members',     label: 'Members',     icon: 'people-outline'    },
    { id: 'departments', label: 'Departments', icon: 'business-outline'  },
    { id: 'performance', label: 'Performance', icon: 'bar-chart-outline' },
    { id: 'insights',    label: 'Insights',    icon: 'bulb-outline'      },
  ];

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F7FA' }}>
        <ActivityIndicator size="large" color="#1a237e" />
        <Text style={{ color: '#78909C', marginTop: 14 }}>Loading analytics…</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
      {/* Header */}
      <LinearGradient colors={['#1a237e','#283593']} style={AS.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={AS.headerBtn}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={AS.headerTitle}>Team Analytics</Text>
          <Text style={AS.headerSub}>{active} active · {departments.length} dept{departments.length !== 1 ? 's' : ''} · {avgPerf}% avg</Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          <TouchableOpacity style={AS.headerBtn}
            onPress={() => navigation.navigate('BusinessVideoConferenceScreen', { companyId })}>
            <Ionicons name="videocam" size={20} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity style={AS.headerBtn} onPress={() => navigation.navigate('TeamManagementScreen')}>
            <Ionicons name="people" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Tabs */}
      <View style={AS.tabBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 6 }}>
          {TABS.map(t => (
            <TouchableOpacity key={t.id} style={[AS.tab, activeTab === t.id && AS.tabActive]}
              onPress={() => setActiveTab(t.id)}>
              <Ionicons name={t.icon} size={15} color={activeTab === t.id ? '#1a237e' : '#90A4AE'} />
              <Text style={[AS.tabLabel, activeTab === t.id && AS.tabLabelActive]}>{t.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <View style={{ flex: 1 }}>
        {activeTab === 'overview'    && renderOverview()}
        {activeTab === 'members'     && renderMembers()}
        {activeTab === 'departments' && renderDepartments()}
        {activeTab === 'performance' && renderPerformance()}
        {activeTab === 'insights'    && renderInsights()}
      </View>

      {renderMemberModal()}
      {renderDeptModal()}
    </SafeAreaView>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const AS = StyleSheet.create({
  header:     { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 14, gap: 10 },
  headerBtn:  { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.15)', alignItems: 'center', justifyContent: 'center' },
  headerTitle:{ fontSize: 18, fontWeight: '900', color: '#fff' },
  headerSub:  { fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 2 },

  tabBar:     { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1', paddingVertical: 4 },
  tab:        { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 9,
    marginHorizontal: 2, borderRadius: 20, gap: 5 },
  tabActive:  { backgroundColor: '#E8EAF6' },
  tabLabel:   { fontSize: 12, color: '#90A4AE', fontWeight: '600' },
  tabLabelActive: { color: '#1a237e', fontWeight: '800' },

  periodRow:  { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10,
    backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1', gap: 8 },
  periodGroup:{ flex: 1, flexDirection: 'row', backgroundColor: '#F5F7FA', borderRadius: 10, padding: 3, gap: 2 },
  periodBtn:  { flex: 1, alignItems: 'center', paddingVertical: 6, borderRadius: 8 },
  periodBtnActive: { backgroundColor: '#1a237e' },
  periodText: { fontSize: 12, color: '#78909C', fontWeight: '700' },
  periodTextActive: { color: '#fff' },
  shareBtn:   { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 7,
    backgroundColor: '#E8EAF6', borderRadius: 10 },

  heroBanner: { margin: 14, borderRadius: 18, padding: 20 },

  kpiGrid:    { flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: 10, gap: 8, marginBottom: 4 },
  kpiCard:    { width: (width - 36) / 2, backgroundColor: '#fff', borderRadius: 14, padding: 14,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.07, shadowRadius: 5, elevation: 2 },
  kpiIcon:    { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
  kpiValue:   { fontSize: 18, fontWeight: '900', color: '#263238', marginTop: 6, marginBottom: 2 },
  kpiLabel:   { fontSize: 11, color: '#78909C', fontWeight: '600' },

  sectionCard:{ backgroundColor: '#fff', marginHorizontal: 14, marginBottom: 10, borderRadius: 16, padding: 16,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 5, elevation: 2 },
  sectionTitle:{ fontSize: 15, fontWeight: '800', color: '#263238' },
  sectionLink:{ fontSize: 13, color: '#1a237e', fontWeight: '700' },

  leaderRow:  { flexDirection: 'row', alignItems: 'center', paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: '#F5F7FA' },
  rank:       { fontSize: 14, fontWeight: '900' },
  lName:      { fontSize: 14, fontWeight: '800', color: '#263238', marginBottom: 1 },
  lSub:       { fontSize: 12, color: '#78909C', marginBottom: 4 },
  lPerf:      { fontSize: 16, fontWeight: '900' },
  lRev:       { fontSize: 11, color: '#90A4AE', marginTop: 2 },

  deptRow:    { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: '#F5F7FA' },
  deptDot:    { width: 12, height: 12, borderRadius: 6, flexShrink: 0 },
  deptName:   { fontSize: 13, fontWeight: '700', color: '#263238' },

  deptCard:   { backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 12,
    borderLeftWidth: 5, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 8, elevation: 3 },
  deptCardIcon:{ width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  deptCardName:{ fontSize: 16, fontWeight: '800', color: '#263238' },

  sortBar:    { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1', paddingVertical: 8 },
  sortBtn:    { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#F5F7FA', borderWidth: 1, borderColor: '#ECEFF1' },
  sortBtnActive: { backgroundColor: '#1a237e', borderColor: '#1a237e' },
  sortBtnText:{ fontSize: 12, color: '#607D8B', fontWeight: '600' },
  sortBtnTextActive: { color: '#fff' },

  memberCard: { backgroundColor: '#fff', borderRadius: 16, padding: 14, marginBottom: 10,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.07, shadowRadius: 5, elevation: 2 },
  rankBadge:  { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', marginRight: 8 },
  rankBadgeText:{ fontSize: 11, fontWeight: '800' },

  metricBox:  { backgroundColor: '#F5F7FA', borderRadius: 12, padding: 12 },
  metricLabel:{ fontSize: 10, color: '#90A4AE', fontWeight: '600', marginBottom: 4, textTransform: 'uppercase' },
  metricValue:{ fontSize: 13, fontWeight: '800', color: '#263238' },

  perfHero:   { margin: 14, borderRadius: 18, padding: 24, alignItems: 'center', gap: 8 },
  perfRow:    { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F5F7FA' },
  perfBadge:  { borderRadius: 10, paddingHorizontal: 10, paddingVertical: 4, marginLeft: 10 },

  statBox:    { backgroundColor: '#F5F7FA', borderRadius: 12, padding: 12, alignItems: 'center' },
  statBoxLabel:{ fontSize: 11, color: '#90A4AE', fontWeight: '600', marginBottom: 4, textTransform: 'uppercase' },
  statBoxValue:{ fontSize: 14, fontWeight: '800' },

  insightCard:{ backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 12,
    borderLeftWidth: 4, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.07, shadowRadius: 5, elevation: 2 },
  insightIcon:{ width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
  insightTitle:{ fontSize: 15, fontWeight: '800', color: '#263238' },
  impactBadge:{ borderRadius: 8, paddingHorizontal: 7, paddingVertical: 2 },
  impactText: { fontSize: 9, color: '#fff', fontWeight: '800', letterSpacing: 0.5 },
  insightBody:{ fontSize: 14, color: '#546E7A', lineHeight: 21, marginBottom: 12, marginTop: 8 },
  insightAction:{ flexDirection: 'row', alignItems: 'flex-start', gap: 8, backgroundColor: '#FFF8E1', borderRadius: 10, padding: 12 },
  insightActionText: { flex: 1, fontSize: 13, color: '#795548', lineHeight: 18 },

  skillChip:  { backgroundColor: '#E3F2FD', borderRadius: 16, paddingHorizontal: 10, paddingVertical: 5,
    flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#BBDEFB' },
  skillText:  { fontSize: 12, color: '#0e4e96', fontWeight: '700' },

  mHeader:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1' },
  mHeaderTitle:{ fontSize: 17, fontWeight: '800', color: '#263238', flex: 1, textAlign: 'center' },
});